import { FadeUp, StaggerContainer, StaggerItem } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const GlobalPortfolioPage = () => {
  const regions = [
    {
      name: "United Arab Emirates",
      code: "UAE",
      focus: "Investment & Commercial Management",
      description: "Knowledge-based investment and management services providing strategic advisory, capital advisory, and commercial management to SMEs and commercial enterprises.",
      image: "https://images.unsplash.com/photo-1770546597346-adb6f350812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBtaW5pbWFsJTIwYmVpZ2V8ZW58MHx8fHwxNzcwNzY2OTY4fDA&ixlib=rb-4.1.0&q=85",
      path: "/portfolio/uae",
      sectors: ["Strategic Advisory", "Capital Advisory", "Property Management", "SME Support"],
    },
    {
      name: "United Kingdom",
      code: "UK",
      focus: "Media, Automotive & Hospitality",
      description: "Diversified portfolio spanning media production, automotive services, and hospitality — delivering professional services across UK and international markets.",
      image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      path: "/portfolio/uk",
      sectors: ["Film & Media Production", "Automotive Services", "Hospitality & Dining"],
    },
    {
      name: "Pakistan",
      code: "PK",
      focus: "Agriculture & Poultry",
      description: "Strategic investments in poultry breeding, farming, and agricultural infrastructure — contributing to food security and sustainable production.",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      path: "/portfolio/pakistan",
      sectors: ["Poultry Breeding", "Agricultural Production", "Hatchery Operations", "Distribution"],
    },
  ];

  const industrySectors = [
    { name: "Investment & Management", region: "UAE" },
    { name: "Film & Audio Post-Production", region: "UK" },
    { name: "Film Processing & Media", region: "UK" },
    { name: "Film Production & Casting", region: "UK" },
    { name: "Automotive Services", region: "UK" },
    { name: "Hospitality & Dining", region: "UK" },
    { name: "Poultry Breeding & Production", region: "Pakistan" },
    { name: "Agricultural Development", region: "Pakistan" },
  ];

  return (
    <div data-testid="global-portfolio-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Global Portfolio</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              Diversified operations across three continents.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              Our group operates across media production, automotive services, hospitality, 
              agriculture, and investment management — each business delivering professional 
              services within its specialist sector.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Portfolio Stats */}
      <section className="pb-24">
        <div className="container-wide">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <FadeUp>
              <div className="p-6 border-glass text-center">
                <span className="text-4xl font-serif text-bronze">8</span>
                <p className="text-sm text-stone mt-2">Industry Sectors</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="p-6 border-glass text-center">
                <span className="text-4xl font-serif text-bronze">3</span>
                <p className="text-sm text-stone mt-2">Operating Regions</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="p-6 border-glass text-center">
                <span className="text-4xl font-serif text-bronze">6</span>
                <p className="text-sm text-stone mt-2">Core Industries</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="p-6 border-glass text-center">
                <span className="text-4xl font-serif text-bronze">∞</span>
                <p className="text-sm text-stone mt-2">Long-term Horizon</p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Region Cards */}
      <section className="pb-24 md:pb-32">
        <div className="container-wide">
          <StaggerContainer className="space-y-24" staggerDelay={0.2}>
            {regions.map((region, index) => (
              <StaggerItem key={region.code}>
                <Link 
                  to={region.path} 
                  className="group block"
                  data-testid={`portfolio-region-${region.code.toLowerCase()}`}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Image */}
                    <div className={`lg:col-span-6 ${index % 2 === 1 ? 'lg:col-start-7' : ''}`}>
                      <div className="aspect-[16/10] relative overflow-hidden border-glass card-reflect">
                        <img
                          src={region.image}
                          alt={region.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-charcoal/10 group-hover:bg-transparent transition-colors duration-500" />
                        
                        {/* Region Code Overlay */}
                        <div className="absolute bottom-6 right-6">
                          <span className="text-6xl md:text-8xl font-serif text-champagne/30 font-normal">
                            {region.code}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-8'}`}>
                      <div className="border-glass card-reflect p-8 transition-all duration-500">
                        <span className="text-mono text-stone/60 mb-4 block">{region.focus}</span>
                        <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6 group-hover:text-bronze transition-colors duration-500">
                          {region.name}
                        </h2>
                        <p className="text-body mb-8">
                          {region.description}
                        </p>

                        {/* Sectors */}
                        <div className="flex flex-wrap gap-3 mb-8">
                          {region.sectors.map((sector) => (
                            <span
                              key={sector}
                              className="px-4 py-2 bg-sand/30 border border-sand/50 text-sm font-sans text-stone backdrop-blur-sm"
                            >
                              {sector}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center gap-2 text-charcoal group-hover:text-bronze transition-colors duration-500">
                          <span className="text-sm font-sans font-medium uppercase tracking-wider">Explore Region</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* All Industry Sectors Grid */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Industry Overview</span>
            <h2 className="text-headline text-champagne mb-12">
              Our operating sectors.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-champagne/10">
            {industrySectors.map((sector, index) => (
              <FadeUp key={sector.name} delay={index * 0.05}>
                <div className="bg-charcoal p-6 h-full">
                  <span className="text-mono text-champagne/40 block mb-2">{sector.region}</span>
                  <h3 className="text-lg font-serif text-champagne">{sector.name}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Philosophy CTA */}
      <section className="section-spacing">
        <div className="container-wide text-center">
          <FadeUp>
            <h2 className="text-headline text-charcoal mb-6">
              Guided by principle, driven by discipline.
            </h2>
            <p className="text-xl font-sans font-light text-stone max-w-2xl mx-auto mb-12">
              Each business operates independently within its specialist sector while 
              contributing to a diversified commercial portfolio.
            </p>
            <Link
              to="/philosophy"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="portfolio-philosophy-link"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Read Our Philosophy</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default GlobalPortfolioPage;
